import { useState, type ReactElement } from 'react';
import classNames from 'classnames';
import { Checkbox, CheckboxGroup, Dropdown, Button } from 'rsuite';
import { MdEmojiEvents, MdGpsFixed, MdKeyboardArrowDown, MdSwapCalls } from 'react-icons/md';

import type { VisaoGeralElasticidadeElasticityLevel, VisaoGeralElasticidadeTableBody } from '../../types';
import { CompetitivenessFilter } from '../CompetitivenessFilter';
import { cpiFilterToApiValue, DEFAULT_CPI_FILTER_STATE, type CpiFilterState } from '../CompetitivenessFilter/competitivenessFilter.utils';
import styles from './SecondFiltersArea.module.scss';

type MenuItem = { eventKey: string; label: string };

const PATH_MENU_ITEMS: MenuItem[] = [
  { eventKey: 'Inelástico', label: 'Inelástico' },
  { eventKey: 'Pouco elástico', label: 'Pouco elástico' },
  { eventKey: 'Elástico', label: 'Elástico' },
  { eventKey: 'Muito elástico', label: 'Muito elástico' },
];

type ToolbarDropdownProps = {
  ariaLabel: string;
  icon: ReactElement;
  children: ReactElement;
  active?: boolean;
  onSelect?: (eventKey: string) => void;
  open?: boolean;
  onToggle?: (open: boolean) => void;
};

const ToolbarDropdown = ({ ariaLabel, icon, children, active, onSelect, open, onToggle }: ToolbarDropdownProps) => (
  <Dropdown
    className={styles.dropdown}
    placement="bottomEnd"
    open={open}
    onToggle={onToggle}
    onSelect={eventKey => onSelect?.(String(eventKey))}
    renderTitle={() => (
      <div
        className={classNames(styles.dropdownTrigger, active && styles.dropdownTriggerActive)}
        aria-label={ariaLabel}
        data-testid={active ? `${ariaLabel}-active` : undefined}
      >
        {icon}
        <MdKeyboardArrowDown size={20} color={active ? '#0D66D0' : '#959595'} aria-hidden />
      </div>
    )}
  >
    {children}
  </Dropdown>
);

export type SecondFiltersAreaProps = {
  productCount?: number;
  onUpdateResults?: () => void;
  hasPendingFilterChanges?: boolean;
  onFiltersClick?: () => void;
  selectedElasticityLevels?: VisaoGeralElasticidadeElasticityLevel[];
  onElasticityLevelsChange?: (levels: VisaoGeralElasticidadeElasticityLevel[]) => void;
  onApplyQuickFilter?: (partial: Partial<VisaoGeralElasticidadeTableBody>) => void;
  appliedElasticityLevels?: VisaoGeralElasticidadeElasticityLevel[];
  appliedCpiOperator?: string;
  appliedOptimalCpiOperator?: string;
};

export const SecondFiltersArea = ({
  productCount = 0,
  onUpdateResults,
  hasPendingFilterChanges = false,
  selectedElasticityLevels = [],
  onElasticityLevelsChange,
  onApplyQuickFilter,
  appliedElasticityLevels = [],
  appliedCpiOperator = '',
  appliedOptimalCpiOperator = '',
}: SecondFiltersAreaProps) => {
  const [elasticityOpen, setElasticityOpen] = useState<boolean>(false);
  const [cpiOpen, setCpiOpen] = useState<boolean>(false);
  const [optimalCpiOpen, setOptimalCpiOpen] = useState<boolean>(false);
  const [selectIsOpen, setSelectIsOpen] = useState<boolean>(false);

  const [cpiFilter, setCpiFilter] = useState<CpiFilterState>(DEFAULT_CPI_FILTER_STATE);
  const [optimalCpiFilter, setOptimalCpiFilter] = useState<CpiFilterState>(DEFAULT_CPI_FILTER_STATE);

  const guardedToggle = (setOpen: (open: boolean) => void) => (open: boolean) => {
    setOpen(selectIsOpen ? true : open);
  };

  const handleApplyCpi = () => {
    const api = cpiFilterToApiValue(cpiFilter);
    onApplyQuickFilter?.({ cpiOperator: api.operator, cpiValueFrom: api.from, cpiValueTo: api.to });
    setCpiOpen(false);
  };

  const handleClearCpi = () => {
    setCpiFilter(DEFAULT_CPI_FILTER_STATE);
    onApplyQuickFilter?.({ cpiOperator: '', cpiValueFrom: 0, cpiValueTo: 0 });
    setCpiOpen(false);
  };

  const handleApplyOptimalCpi = () => {
    const api = cpiFilterToApiValue(optimalCpiFilter);
    onApplyQuickFilter?.({ optimalCpiOperator: api.operator, optimalCpiValueFrom: api.from, optimalCpiValueTo: api.to });
    setOptimalCpiOpen(false);
  };

  const handleClearOptimalCpi = () => {
    setOptimalCpiFilter(DEFAULT_CPI_FILTER_STATE);
    onApplyQuickFilter?.({ optimalCpiOperator: '', optimalCpiValueFrom: 0, optimalCpiValueTo: 0 });
    setOptimalCpiOpen(false);
  };

  return (
    <section className={styles.bar} aria-label="Filtros rápidos">
      <Button appearance="primary" className={styles['update-results-btn']} onClick={onUpdateResults} disabled={!hasPendingFilterChanges}>
        Atualizar resultados
      </Button>
      <div className={styles['results-count-wrapper']}>
        <p className={styles.count}>
          <span className={styles.countStrong}>{productCount}</span>
          <span> produtos encontrados</span>
        </p>
      </div>

      <div className={styles.actions}>
        <ToolbarDropdown
          ariaLabel="Filtro de caminho"
          icon={<MdSwapCalls size={20} color={appliedElasticityLevels.length > 0 ? '#0D66D0' : '#959595'} />}
          active={appliedElasticityLevels.length > 0}
          open={elasticityOpen}
          onToggle={setElasticityOpen}
        >
          <Dropdown.Item panel className={styles['elasticidade-dropdown-content']}>
            <div className={styles['elasticidade-dropdown-title-wrapper']}>
              <p>Filtrar produtos com elasticidade cruzada</p>
            </div>
            <CheckboxGroup value={selectedElasticityLevels} onChange={value => onElasticityLevelsChange?.(value as VisaoGeralElasticidadeElasticityLevel[])}>
              {PATH_MENU_ITEMS.map(item => (
                <Checkbox className={styles['dropdown-item']} key={item.eventKey} value={item.eventKey}>
                  {item.label}
                </Checkbox>
              ))}
            </CheckboxGroup>
          </Dropdown.Item>
        </ToolbarDropdown>

        <ToolbarDropdown
          ariaLabel="Filtro de competitividade atual"
          icon={<MdEmojiEvents size={20} color={appliedCpiOperator !== '' ? '#0D66D0' : '#959595'} />}
          active={appliedCpiOperator !== ''}
          open={cpiOpen}
          onToggle={guardedToggle(setCpiOpen)}
        >
          <CompetitivenessFilter
            title="Filtrar produtos com competitividade"
            testIdPrefix="cpi-filter"
            value={cpiFilter}
            onChange={setCpiFilter}
            onApply={handleApplyCpi}
            onClear={handleClearCpi}
            onSelectOpenChange={setSelectIsOpen}
          />
        </ToolbarDropdown>

        {/* <ToolbarDropdown
          ariaLabel="Filtro de competitividade ótima"
          icon={<MdGpsFixed size={20} color={appliedOptimalCpiOperator !== '' ? '#0D66D0' : '#959595'} />}
          active={appliedOptimalCpiOperator !== ''}
          open={optimalCpiOpen}
          onToggle={guardedToggle(setOptimalCpiOpen)}
        >
          <CompetitivenessFilter
            title="Filtrar produtos com competitividade ótima"
            testIdPrefix="optimal-cpi-filter"
            value={optimalCpiFilter}
            onChange={setOptimalCpiFilter}
            onApply={handleApplyOptimalCpi}
            onClear={handleClearOptimalCpi}
            onSelectOpenChange={setSelectIsOpen}
          />
        </ToolbarDropdown> */}
      </div>
    </section>
  );
};
